import { useEffect, useRef, useState } from "react";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Box, Menu, MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IconInvalidUrl, IconPlay } from "@app/components/icons";
import { DataTooltip } from "@app/components/atoms";
import { format } from "date-fns";
import { useAppContext } from "@app/AppContext";
import PdfFileIcon from "@app/components/icons/PdfFileIcon/PdfFileIcon";

interface MediaUploadMenuProps {
  media: File | (File & { duration: number }) | any;
  index: number;
  onUploadFile: (
    e: React.ChangeEvent<HTMLInputElement>,
    action: string,
    index?: number
  ) => void;
  deleteFile: (index: number) => void;
  typeFile: string;
  onPreviewMedia?: (mediaUrl: string | string[]) => void;
}

export const MediaUploadMenuMachineReportHistory = ({
  media,
  index,
  onUploadFile,
  deleteFile,
  typeFile,
  onPreviewMedia,
}: MediaUploadMenuProps) => {
  const [isOpenActionMenu, setIsOpenActionMenu] = useState(false);
  const { t } = useTranslation();
  const anchorElRef = useRef(null);

  const [mediaSrc, setMediaSrc] = useState<string | null>(null);

  useEffect(() => {
    const url = media?.mimeType?.includes("video")
      ? media.thumbnailUrl ||
      (media instanceof File ? URL.createObjectURL(media) : "")
      : media.mediaUrl ||
      (media instanceof File ? URL.createObjectURL(media) : "");

    setMediaSrc(url);

    return () => {
      if (url?.startsWith("blob:")) {
        URL.revokeObjectURL(url);
      }
    };
  }, [media]);

  const handleCloseMenu = () => {
    setIsOpenActionMenu(false);
  };

  const handlePreview = () => {
    try {
      if (onPreviewMedia) {
        if (Array.isArray(media)) {
          const mediaUrls = media.map(
            (file: any) => file.mediaUrl || URL.createObjectURL(file)
          );

          onPreviewMedia(mediaUrls);
        } else {
          const mediaUrl = media?.mediaUrl || URL.createObjectURL(media);

          if (mediaUrl) {
            onPreviewMedia(mediaUrl);
          }
        }
        return;
      }

      if (Array.isArray(media)) {
        const mediaUrls = media.map(
          (file: any) => file.mediaUrl || URL.createObjectURL(file)
        );

        mediaUrls.forEach((url) => {
          window.open(url, "_blank", "noopener,noreferrer");
        });
      } else {
        const mediaUrl = media?.mediaUrl || URL.createObjectURL(media);

        if (mediaUrl) {
          window.open(mediaUrl, "_blank", "noopener,noreferrer");
        }
      }
    } catch (error) {
      console.error(
        "❌ An error occurred while handling the media preview:",
        error
      );
    }
  };

  const { userTimeFormat } = useAppContext();

  return (
    <div
      className="preview-container pointer"
      key={`${media?.name || media?.fileName}_${media?.lastModified}_${index}`}
      style={{
        width: "100%",
        maxWidth: "1000px",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px",
        border: "1px solid #ddd",
        borderRadius: "10px",
        backgroundColor: "#f9f9f9",
        flexWrap: "wrap",
        gap: "5px",
      }}
      onClick={() => setIsOpenActionMenu(!isOpenActionMenu)}
    >
      <div
        style={{
          width: "150px",
          height: "150px",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        {mediaSrc ? (
          media?.mimeType?.includes("video") ||
            media?.type?.includes("video") ? (
            <div
              style={{ position: "relative", width: "150px", height: "150px" }}
            >
              <img
                src={media?.poster || mediaSrc}
                alt="video thumbnail"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
                onError={() => setMediaSrc(null)}
              />
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  backgroundColor: "rgba(0,0,0,0.4)",
                  borderRadius: "50%",
                  width: "36px",
                  height: "36px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <IconPlay />
              </div>
            </div>
          ) : media?.mimeType?.includes("pdf") ||
            media?.type?.includes("pdf") ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
                backgroundColor: "#f2f2f2",
              }}
            >
              <PdfFileIcon style={{ fontSize: 64, color: "#d32f2f" }} />
              <span
                style={{
                  fontSize: "12px",
                  color: "#555",
                  textAlign: "center",
                  marginTop: "4px",
                }}
              >
                {t("pdfFile")}
              </span>
            </div>
          ) : (
            <img
              src={mediaSrc}
              alt="preview"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                borderRadius: "8px",
              }}
              onError={() => setMediaSrc(null)}
            />
          )
        ) : (
          <Box
            className="invalid-src"
            sx={{
              width: 60,
              height: 60,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <IconInvalidUrl />
          </Box>
        )}
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          marginInlineStart: "10px",
          maxWidth: "calc(100% - 220px)",
          overflow: "hidden",
          flexWrap: "wrap",
        }}
      >
        <p
          style={{
            fontSize: "12px",
            fontWeight: "normal",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "100%",
            marginBottom: "2px",
          }}
          title={media?.name || media?.fileName}
        >
          {media?.name || media?.fileName}
        </p>

        <p
          style={{
            fontSize: "12px",
            color: "#888",
            marginTop: "0px",
            lineHeight: "1.2",
          }}
        >
          <DataTooltip isTextPre width="250px">
            {(() => {
              const name = media?.name || media?.fileName || "";
              if (name.includes("sample-image")) return "";

              const capture = media?.captureMediaAt;
              const modified = media?.lastModifiedFormatted;

              if (
                typeof capture === "string" &&
                capture.trim() !== "" &&
                !isNaN(Date.parse(capture))
              ) {
                const parsed = new Date(capture);
                if (parsed.getFullYear() > 1971) {
                  return format(parsed, userTimeFormat);
                }
              }

              if (
                typeof modified === "string" &&
                modified.trim() !== "" &&
                !isNaN(Date.parse(modified))
              ) {
                const parsed = new Date(modified);
                if (parsed.getFullYear() > 1971) {
                  return format(parsed, userTimeFormat);
                }
              }

              return "";
            })()}
          </DataTooltip>
        </p>
      </div>

      <div
        ref={anchorElRef}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: "30px",
          marginLeft: "auto",
          padding: "0 10px",
        }}
      >
        {isOpenActionMenu ? (
          <KeyboardArrowUp className="arrow-icon txt-gray-1 fs-20" />
        ) : (
          <KeyboardArrowDown className="arrow-icon txt-gray-1 fs-20" />
        )}
      </div>

      <Menu
        sx={{ mt: "40px" }}
        anchorEl={anchorElRef.current}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        open={isOpenActionMenu}
        onClose={() => setIsOpenActionMenu(false)}
        className="menu-preview-file"
      >
        <MenuItem disableRipple className="menu-item">
          <label htmlFor={`replace-file${index}`}>
            <span className="fs-16 txt-black-1 pointer">{t("chooseFile")}</span>
            <input
              type="file"
              accept={typeFile}
              className="btn-secondary"
              id={`replace-file${index}`}
              hidden
              onChange={(e) => {
                e.stopPropagation();
                onUploadFile(e, "replace", index);
                setIsOpenActionMenu(false);
              }}
            />
          </label>
        </MenuItem>

        <MenuItem
          disableRipple
          className="menu-item"
          onClick={(e) => {
            e.stopPropagation();

            handlePreview();
            handleCloseMenu();
          }}
        >
          <span className="fs-16 txt-black-1 pointer">{t("preview")}</span>
        </MenuItem>

        <MenuItem
          disableRipple
          className="menu-item"
          onClick={() => deleteFile(index)}
        >
          <span className="fs-16 txt-red pointer">{t("deleteFile")}</span>
        </MenuItem>
      </Menu>
    </div>
  );
};
